class PublicPaymentApiError extends Error {
    constructor({ code, message, status, retryAfter, errors }) {
        super(message);
        this.name = 'PublicPaymentApiError';
        this.code = code;
        this.status = status;
        this.retryAfter = retryAfter;
        this.errors = errors || null;
    }
}

const publicPaymentErrorCodes = new Set([
    'VALIDATION_FAILED',
    'CONTRACT_NOT_FOUND',
    'PAYMENT_FORBIDDEN',
    'PAYMENT_CONTEXT_STALE',
    'PAYMENT_IN_PROGRESS',
    'IEAC_UNAVAILABLE',
    'PAYMENT_PROVIDER_UNAVAILABLE',
    'RATE_LIMITED',
]);

const getRetryAfter = (response) => {
    const header = response?.headers?.get?.('Retry-After');
    if (typeof header !== 'string' || !/^\d+$/.test(header.trim())) return null;

    const retryAfter = Number.parseInt(header, 10);
    return Number.isInteger(retryAfter) && retryAfter > 0 ? retryAfter : null;
};

const malformedResponseError = (response) => new PublicPaymentApiError({
    code: 'MALFORMED_RESPONSE',
    message: 'Не удалось обработать ответ сервера',
    status: Number.isInteger(response?.status) ? response.status : null,
    retryAfter: getRetryAfter(response),
    errors: null,
});

const requestJson = async (url, options, fetchImpl) => {
    const request = fetchImpl || globalThis.fetch;
    if (typeof request !== 'function') {
        throw new PublicPaymentApiError({
            code: 'NETWORK_ERROR',
            message: 'Не удалось отправить запрос. Попробуйте позднее',
            status: null,
            retryAfter: null,
            errors: null,
        });
    }

    let response;
    try {
        response = await request(url, options);
    } catch (error) {
        throw new PublicPaymentApiError({
            code: 'NETWORK_ERROR',
            message: 'Не удалось отправить запрос. Попробуйте позднее',
            status: null,
            retryAfter: null,
            errors: null,
        });
    }

    let body;
    try {
        body = await response.json();
    } catch (error) {
        throw malformedResponseError(response);
    }

    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        throw malformedResponseError(response);
    }

    if (response.ok && body.success === true) return body;

    if (
        body.success === false
        && publicPaymentErrorCodes.has(body.code)
        && typeof body.message === 'string'
        && body.message.trim()
    ) {
        throw new PublicPaymentApiError({
            code: body.code,
            message: body.message,
            status: response.status,
            retryAfter: getRetryAfter(response),
            errors: body.errors && typeof body.errors === 'object' ? body.errors : null,
        });
    }

    throw malformedResponseError(response);
};

const readPaymentLinkToken = (routeQuery, locationSearch) => {
    if (routeQuery && typeof routeQuery === 'object') {
        const routeToken = routeQuery.c;
        if (typeof routeToken === 'string' && routeToken.trim()) {
            return routeToken.trim();
        }
    }

    if (typeof locationSearch === 'string' && locationSearch) {
        const searchToken = new URLSearchParams(locationSearch).get('c');
        if (typeof searchToken === 'string' && searchToken.trim()) {
            return searchToken.trim();
        }
    }

    return '';
};

const decodeContractToken = async (url, token, fetchImpl) => {
    const response = await requestJson(
        url,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ c: token }),
        },
        fetchImpl
    );

    const number = response?.number;
    if (typeof number !== 'string' || !number.trim()) {
        throw malformedResponseError({ status: null });
    }

    return number.trim();
};

const createRequestId = (cryptoImpl) => {
    const crypto = cryptoImpl || globalThis.crypto;
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
    if (typeof crypto?.getRandomValues !== 'function') {
        throw new Error('Secure random values are unavailable');
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.prototype.map.call(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

module.exports = {
    PublicPaymentApiError,
    requestJson,
    decodeContractToken,
    readPaymentLinkToken,
    createRequestId,
};
